<template>
  <validation-observer ref="formObserver">
    <b-form @submit.prevent="validationForm" class="form-container">
      <b-col>
        <h4 class="mb-2">Konfiguracja</h4>
        <b-card>
          <b-form-group label="Język" label-for="v-language-select">
            <validation-provider #default="{ errors }" name="Język" rules="required">
              <v-select
                id="v-language-select"
                label="value"
                placeholder="Wybierz język"
                v-model="lang"
                :clearable="false"
                :deselectFromDropdown="false"
                :options="languages"
                @input="fetchByLocale" />
              <small class="text-danger" :class="errors[0] ? 'error-shown' : ''">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group label="Nazwa" label-for="v-name-main">
            <validation-provider name="Nazwa" rules="required|min:2|max:100" v-slot="{ errors }">
              <b-form-input id="v-name-main" placeholder="Nazwa" v-model="form.name" />
              <small class="text-danger" :class="errors[0] ? 'error-shown' : ''">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group label="Slug (pozostaw pusty, aby wygenerować automatycznie)" label-for="v-slug-main">
            <validation-provider name="Slug głównej kategorii" rules="min:2|max:100" v-slot="{ errors }">
              <b-form-input id="v-slug-main" placeholder="Slug" v-model="form.slug" />
              <small class="text-danger" :class="errors[0] ? 'error-shown' : ''">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group label="Pozycja (zarządzaj pozycjami z poziomu tabeli)" label-for="v-position-main">
            <validation-provider name="Pozycja" rules="numeric" v-slot="{ errors }">
              <b-form-input id="v-position-main" placeholder="Pozycja" v-model="form.position" disabled />
              <small class="text-danger" :class="errors[0] ? 'error-shown' : ''">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group label="Link do inspiracji" label-for="v-inspiration">
            <validation-provider name="Link do inspiracji" rules="required|min:2|max:100" v-slot="{ errors }">
              <b-form-input id="v-name-main" placeholder="Link do inspiracji" v-model="form.inspiration_link" />
              <small class="text-danger" :class="errors[0] ? 'error-shown' : ''">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group for="v-logo" label="Logo">
            <b-button @click="toggleShowModal('main_image')" :disabled="!lang"> Wybierz zdjęcie </b-button>
            <validation-provider #default="{ errors }" name="main_image" rules="required|size:5000">
              <!-- workaround for validation-provider -->
              <input v-if="files.main_image" type="hidden" name="main_image" v-model="files.main_image.file" />
              <input v-else type="hidden" name="main_image" v-model="files.main_image" />

              <ImageSelector
                v-if="showImagesModal.main_image"
                :files="filesArray"
                :show-modal="showImagesModal.main_image"
                @closeModal="toggleShowModal('main_image')"
                @submit="(fileList) => handleFile(fileList[0], 'main_image')" />
              <small class="text-danger d-block">{{ errors[0] }}</small>
            </validation-provider>

            <ImagePreview
              v-if="files.main_image"
              :image="files.main_image"
              @deleteImage="() => handleDelete('main_image')" />
          </b-form-group>
        </b-card>

        <h4 class="mb-2 mt-4">Dane podkategorii</h4>

        <b-card v-for="(subgroup, index) in form.navigation_subgroups" :key="index" class="mb-1">
          <b-card-title class="d-flex justify-content-between align-items-center">
            <div>
              <h5 class="m-0">{{ subgroup.name ? subgroup.name : `Grupa ${index + 1}` }}</h5>
            </div>
            <div class="d-flex">
              <span :class="`sub-${index}`" v-b-toggle="`sub-${index}`">
                <feather-icon class="edit-icon" icon="EditIcon" />
              </span>
              <span>
                <feather-icon class="delete-icon ml-1" icon="TrashIcon" @click="removeGroup(index)" />
              </span>
            </div>
          </b-card-title>
          <div class="mt-2">
            <b-form-group label="Nazwa grupy" :label-for="`v-group-name-${index}`">
              <validation-provider name="Nazwa grupy" rules="required|min:2|max:100" v-slot="{ errors }">
                <b-form-input :id="`v-group-name-${index}`" placeholder="Nazwa grupy" v-model="subgroup.name" />
                <small class="text-danger" :class="errors[0] ? 'error-shown' : ''">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group
              label="Slug (pozostaw pusty, aby wygenerować automatycznie)"
              :label-for="`v-group-slug-${index}`">
              <validation-provider :name="`Slug grupy ${index + 1}`" rules="min:2|max:100" v-slot="{ errors }">
                <b-form-input :id="`v-group-slug-${index}`" placeholder="Slug" v-model="subgroup.slug" />
                <small class="text-danger" :class="errors[0] ? 'error-shown' : ''">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-form-group class="mt-2">
              <label :for="`v-subgroup-custom-filtering-${index}`" class="d-flex justify-content-between align-items-center">
                <span>Filtrowanie</span>
                <b-button
                  variant="outline-info"
                  size="sm"
                  @click="openFilterFieldsModal('subgroup', index)">
                  Zobacz pola
                </b-button>
              </label>
              <validation-provider
                :name="`Filtrowanie grupy ${index + 1}`"
                v-slot="{ errors }">
                <div class="d-flex align-items-end">
                  <b-form-input
                    :id="`v-subgroup-filter-field-${index}`"
                    placeholder="Nazwa pola"
                    v-model="subgroup.filter_field"
                    class="mr-2" />
                  <b-form-select
                    :id="`v-subgroup-filter-operator-${index}`"
                    v-model="subgroup.filter_operator"
                    :options="filterOperators"
                    class="mr-2"
                    style="min-width: 120px;" />
                  <b-form-input
                    :id="`v-subgroup-filter-value-${index}`"
                    placeholder="Wartość"
                    v-model="subgroup.filter_value" />
                </div>
                <small class="text-danger" :data-collapse-id="`sub-${index}`" :class="errors[0] ? 'error-shown' : ''">{{
                  errors[0]
                }}</small>
              </validation-provider>
            </b-form-group>

            <b-collapse :id="`sub-${index}`">
              <div v-for="(navEl, subIdx) in subgroup.navigation_elements" :key="`sub-${subIdx}`" class="pl-2 mt-5">
                <b-card-title class="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    <h6 class="m-0">
                      {{ subgroup.name ? subgroup.name : `nazwa grupy` }},
                      {{ navEl.name ? navEl.name : 'nazwa kategorii' }}
                    </h6>
                  </div>
                  <div>
                    <feather-icon class="delete-icon" icon="TrashIcon" @click="removeCategory(index, subIdx)" />
                  </div>
                </b-card-title>

                <div class="mt-2">
                  <b-form-group label="Nazwa" :label-for="`v-subgroup-name-${index}-${subIdx}`">
                    <validation-provider
                      :name="`Nazwa grupy ${index + 1}, kategorii ${subIdx + 1}`"
                      rules="required|min:2|max:100"
                      v-slot="{ errors }">
                      <b-form-input
                        :id="`v-subgroup-name-${index}-${subIdx}`"
                        placeholder="Nazwa"
                        v-model="navEl.name" />
                      <small
                        class="text-danger"
                        :class="errors[0] ? 'error-shown' : ''"
                        :data-collapse-id="`sub-${index}`">
                        {{ errors[0] }}
                      </small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group
                    label="Slug (pozostaw pusty, aby wygenerować automatycznie)"
                    :label-for="`v-subgroup-slug-${index}-${subIdx}`">
                    <validation-provider
                      :name="`Slug grupy ${index + 1}, kateogrii ${subIdx + 1}`"
                      rules="min:2|max:100"
                      v-slot="{ errors }">
                      <b-form-input
                        :id="`v-subgroup-slug-${index}-${subIdx}`"
                        placeholder="Slug"
                        v-model="navEl.slug" />
                      <small
                        class="text-danger"
                        :data-collapse-id="`sub-${index}`"
                        :class="errors[0] ? 'error-shown' : ''"
                        >{{ errors[0] }}</small
                      >
                    </validation-provider>
                  </b-form-group>

                  <b-form-group label="Kategorie" label-for="v-category-select">
                    <validation-provider
                      :name="`Kategoria ${index + 1}-${subIdx + 1}`"
                      rules="required"
                      v-slot="{ errors }">
                      <v-select
                        label="name"
                        :name="`Kategoria ${index + 1}-${subIdx + 1}`"
                        placeholder="Wybierz kategorię"
                        v-model="navEl.categories"
                        :id="`v-subgroup-categories-${index}-${subIdx}`"
                        :clearable="false"
                        :closeOnSelect="false"
                        :multiple="true"
                        :deselectFromDropdown="false"
                        :options="subcategories" />
                      <small
                        class="text-danger"
                        :class="errors[0] ? 'error-shown' : ''"
                        :data-collapse-id="`sub-${index}`">
                        {{ errors[0] }}
                      </small>
                    </validation-provider>

                    <b-form-group class="mt-1">
                      <label :for="`v-subgroup-custom-filtering-${index}-${subIdx}`" class="d-flex justify-content-between align-items-center">
                        <span>Filtrowanie</span>
                        <b-button
                          variant="outline-info"
                          size="sm"
                          @click="openFilterFieldsModal('element', index, subIdx)">
                          Zobacz pola
                        </b-button>
                      </label>
                      <validation-provider
                        :name="`Filtrowanie grupy ${index + 1}, kateogrii ${subIdx + 1}`"
                        v-slot="{ errors }">
                        <div class="d-flex align-items-end">
                          <b-form-input
                            :id="`v-subgroup-filter-field-${index}-${subIdx}`"
                            placeholder="Nazwa pola"
                            v-model="navEl.filter_field"
                            class="mr-2" />
                          <b-form-select
                            :id="`v-subgroup-filter-operator-${index}-${subIdx}`"
                            v-model="navEl.filter_operator"
                            :options="filterOperators"
                            class="mr-2"
                            style="min-width: 120px;" />
                          <b-form-input
                            :id="`v-subgroup-filter-value-${index}-${subIdx}`"
                            placeholder="Wartość"
                            v-model="navEl.filter_value" />
                        </div>
                        <small
                          class="text-danger"
                          :data-collapse-id="`sub-${index}`"
                          :class="errors[0] ? 'error-shown' : ''"
                          >{{ errors[0] }}</small
                        >
                      </validation-provider>
                    </b-form-group>
                  </b-form-group>
                </div>
              </div>
              <div class="d-flex justify-content-end">
                <b-button class="mt-2 ml-2" size="sm" variant="info" @click="addCategory(index)">
                  Dodaj kategorię
                </b-button>
              </div>
            </b-collapse>
          </div>
        </b-card>
        <div class="d-flex mt-2 justify-content-end">
          <b-button variant="info" @click="addGroup">Dodaj grupę</b-button>
          
          <b-dropdown
            class="ml-2"
            variant="outline-primary"
            :disabled="!form.id"
            right
            text="Tłumacz z AI">
            <b-dropdown-item
              v-for="language in availableTargetLanguages"
              :key="language.lang"
              @click="onTranslateFrom(language)">
              {{ `Tłumacz z języka - ${language.value}` }}
            </b-dropdown-item>
          </b-dropdown>
          
          <b-button class="ml-1" variant="success" type="submit">Zapisz</b-button>
        </div>
      </b-col>
    </b-form>
    
    <FilterFieldsModal
      ref="filterFieldsModal"
      @field-selected="onFieldSelected" />
  </validation-observer>
</template>

<script>
import NotificationMixin from '@/mixins/NotificationMixin';

import { getLanguages } from '@/constants/languages';
import FileUploadMixin from '@/mixins/FileUploadMixin.vue';

import ImagePreview from '@/components/shared/ImagePreview.vue';
import ImageSelector from '@/components/shared/ImageSelector.vue';
import FilterFieldsModal from '@/components/shared/FilterFieldsModal.vue';
import { aiTranslateProductNavigation } from '@/http/navigation';

export default {
  mixins: [FileUploadMixin, NotificationMixin],
  props: {
    navigationGroup: {
      type: Object,
      required: true,
    },
    productSubcategories: {
      type: Array,
      required: true,
    },
  },
  components: {
    ImagePreview,
    ImageSelector,
    FilterFieldsModal,
  },

  data() {
    return {
      form: this.navigationGroup,
      lang: {
        lang: 'pl',
        value: 'Polski',
      },
      languages: getLanguages(),
      subcategories: this.productSubcategories,
      filterOperators: [
        { value: '=', text: 'Równe' },
        { value: 'like', text: 'Zawiera' },
      ],
      currentFilterContext: null, // { type: 'subgroup'|'element', subgroupIndex, elementIndex? }
    };
  },

  created() {
    this.setFileUploadData(['main_image'], ['main_image']);
  },

  watch: {
    async navigationGroup() {
      await this.setImage(this.navigationGroup.main_image, 'main_image');

      this.form = this.navigationGroup;
      this.parseFilterStrings();
    },
    productSubcategories: {
      handler() {
        this.subcategories = this.productSubcategories;
      },
      deep: true,
    },
    form: {
      handler() {
        this.generateFilterStrings();
      },
      deep: true,
    },
  },

  methods: {
    removeGroup(index) {
      this.form.navigation_subgroups.splice(index, 1);
    },
    removeCategory(index, subIdx) {
      this.form.navigation_subgroups[index].navigation_elements.splice(subIdx, 1);
    },
    addCategory(index) {
      this.navigationGroup.navigation_subgroups[index].navigation_elements.push({
        name: '',
        categories: [],
        slug: '',
        filter_field: '',
        filter_operator: '=',
        filter_value: '',
      });
    },
    addGroup() {
      this.navigationGroup.navigation_subgroups.push({
        name: '',
        navigation_elements: [],
        slug: '',
        filter_field: '',
        filter_operator: '=',
        filter_value: '',
      });
    },
    parseFilterStrings() {
      // Parse subgroup filters
      if (this.form.navigation_subgroups) {
        this.form.navigation_subgroups.forEach(subgroup => {
          if (subgroup.custom_filtering) {
            const parts = subgroup.custom_filtering.split(';');
            if (parts.length === 3) {
              subgroup.filter_field = parts[0];
              subgroup.filter_operator = parts[1];
              subgroup.filter_value = parts[2];
            }
          }
          
          // Parse element filters
          if (subgroup.navigation_elements) {
            subgroup.navigation_elements.forEach(element => {
              if (element.custom_filtering) {
                const parts = element.custom_filtering.split(';');
                if (parts.length === 3) {
                  element.filter_field = parts[0];
                  element.filter_operator = parts[1];
                  element.filter_value = parts[2];
                }
              }
            });
          }
        });
      }
    },
    generateFilterStrings() {
      // Generate subgroup filter strings
      if (this.form.navigation_subgroups) {
        this.form.navigation_subgroups.forEach(subgroup => {
          if (subgroup.filter_field && subgroup.filter_operator && subgroup.filter_value) {
            subgroup.custom_filtering = `${subgroup.filter_field};${subgroup.filter_operator};${subgroup.filter_value}`;
          } else {
            subgroup.custom_filtering = '';
          }
          
          // Generate element filter strings
          if (subgroup.navigation_elements) {
            subgroup.navigation_elements.forEach(element => {
              if (element.filter_field && element.filter_operator && element.filter_value) {
                element.custom_filtering = `${element.filter_field};${element.filter_operator};${element.filter_value}`;
              } else {
                element.custom_filtering = '';
              }
            });
          }
        });
      }
    },
    fetchByLocale() {
      this.$emit('fetchByLocale', this.lang);
    },
    onSubmit() {
      const data = {
        ...this.form,
        main_image: this.files.main_image,
        lang: this.lang.lang,
      };

      this.$emit('onSubmit', data);
    },
    onTranslateFrom(language) {
      if (!this.form.id) return;
      
      // Źródło: język wybrany w dropdownie, Cel: aktualnie wybrany w polu "Język"
      const fromLocale = this.languages.find((l) => l.lang === language.lang)?.locale;
      const toLocale = this.languages.find((l) => l.lang === this.lang.lang)?.locale;

      if (!fromLocale || !toLocale || fromLocale === toLocale) {
        // this.notifyError('Błąd AI', 'Wybierz różne języki źródłowy i docelowy');
        return;
      }

      aiTranslateProductNavigation(this.form.id, fromLocale, toLocale)
        .then(({ data }) => {
          this.populateTranslations(data);
          // this.notifySuccess('Sukces', 'Pobrano tłumaczenia AI');
        })
        .catch((err) => {
          const message = err?.response?.data?.message || 'Nie udało się pobrać tłumaczeń AI';
          // this.notifyError('Błąd AI', message);
        });
    },
    populateTranslations(translations) {
      // Main group translations
      if (translations.group_name) this.form.name = translations.group_name;
      if (translations.group_title) this.form.title = translations.group_title;
      if (translations.group_description) this.form.description = translations.group_description;
      if (translations.group_slug) this.form.slug = translations.group_slug;

      // Subgroups translations
      Object.keys(translations).forEach(key => {
        if (key.startsWith('subgroup_') && key.includes('_name')) {
          const match = key.match(/subgroup_(\d+)_name/);
          if (match) {
            const subgroupIndex = parseInt(match[1]);
            if (this.form.navigation_subgroups[subgroupIndex]) {
              this.form.navigation_subgroups[subgroupIndex].name = translations[key];
            }
          }
        }
        if (key.startsWith('subgroup_') && key.includes('_title')) {
          const match = key.match(/subgroup_(\d+)_title/);
          if (match) {
            const subgroupIndex = parseInt(match[1]);
            if (this.form.navigation_subgroups[subgroupIndex]) {
              this.form.navigation_subgroups[subgroupIndex].title = translations[key];
            }
          }
        }
        if (key.startsWith('subgroup_') && key.includes('_description')) {
          const match = key.match(/subgroup_(\d+)_description/);
          if (match) {
            const subgroupIndex = parseInt(match[1]);
            if (this.form.navigation_subgroups[subgroupIndex]) {
              this.form.navigation_subgroups[subgroupIndex].description = translations[key];
            }
          }
        }
        if (key.startsWith('subgroup_') && key.includes('_slug') && !key.includes('_element_')) {
          const match = key.match(/subgroup_(\d+)_slug/);
          if (match) {
            const subgroupIndex = parseInt(match[1]);
            if (this.form.navigation_subgroups[subgroupIndex]) {
              this.form.navigation_subgroups[subgroupIndex].slug = translations[key];
            }
          }
        }

        // Elements translations
        if (key.startsWith('subgroup_') && key.includes('_element_') && key.includes('_name')) {
          const match = key.match(/subgroup_(\d+)_element_(\d+)_name/);
          if (match) {
            const subgroupIndex = parseInt(match[1]);
            const elementIndex = parseInt(match[2]);
            if (this.form.navigation_subgroups[subgroupIndex] && 
                this.form.navigation_subgroups[subgroupIndex].navigation_elements[elementIndex]) {
              this.form.navigation_subgroups[subgroupIndex].navigation_elements[elementIndex].name = translations[key];
            }
          }
        }
        if (key.startsWith('subgroup_') && key.includes('_element_') && key.includes('_title')) {
          const match = key.match(/subgroup_(\d+)_element_(\d+)_title/);
          if (match) {
            const subgroupIndex = parseInt(match[1]);
            const elementIndex = parseInt(match[2]);
            if (this.form.navigation_subgroups[subgroupIndex] && 
                this.form.navigation_subgroups[subgroupIndex].navigation_elements[elementIndex]) {
              this.form.navigation_subgroups[subgroupIndex].navigation_elements[elementIndex].title = translations[key];
            }
          }
        }
        if (key.startsWith('subgroup_') && key.includes('_element_') && key.includes('_description')) {
          const match = key.match(/subgroup_(\d+)_element_(\d+)_description/);
          if (match) {
            const subgroupIndex = parseInt(match[1]);
            const elementIndex = parseInt(match[2]);
            if (this.form.navigation_subgroups[subgroupIndex] && 
                this.form.navigation_subgroups[subgroupIndex].navigation_elements[elementIndex]) {
              this.form.navigation_subgroups[subgroupIndex].navigation_elements[elementIndex].description = translations[key];
            }
          }
        }
        if (key.startsWith('subgroup_') && key.includes('_element_') && key.includes('_slug')) {
          const match = key.match(/subgroup_(\d+)_element_(\d+)_slug/);
          if (match) {
            const subgroupIndex = parseInt(match[1]);
            const elementIndex = parseInt(match[2]);
            if (this.form.navigation_subgroups[subgroupIndex] && 
                this.form.navigation_subgroups[subgroupIndex].navigation_elements[elementIndex]) {
              this.form.navigation_subgroups[subgroupIndex].navigation_elements[elementIndex].slug = translations[key];
            }
          }
        }
      });
    },
    validationForm() {
      this.$refs.formObserver.validate().then((success) => {
        if (success) {
          return this.onSubmit();
        }

        const elementToScrollTo = document.querySelector('.error-shown');

        if (elementToScrollTo) {
          const collapseElement = document.querySelector(`#${elementToScrollTo.dataset.collapseId}`);

          if (collapseElement && !collapseElement.classList.contains('show')) {
            const collapseBtn = document.querySelector(`.${elementToScrollTo.dataset.collapseId}`);
            collapseBtn.click();

            return setTimeout(() => {
              elementToScrollTo.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500);
          }
        }

        elementToScrollTo.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // this.showFormValidationNotification();
      });
    },
    openFilterFieldsModal(type, subgroupIndex, elementIndex = null) {
      this.currentFilterContext = {
        type,
        subgroupIndex,
        elementIndex,
      };
      this.$refs.filterFieldsModal.show();
    },
    onFieldSelected(field) {
      if (!this.currentFilterContext) return;
      
      const { type, subgroupIndex, elementIndex } = this.currentFilterContext;
      
      if (type === 'subgroup') {
        // Ustaw pole dla podgrupy - używamy $set dla reaktywności
        if (this.form.navigation_subgroups && this.form.navigation_subgroups[subgroupIndex]) {
          this.$set(this.form.navigation_subgroups[subgroupIndex], 'filter_field', field.field);
        }
      } else if (type === 'element' && elementIndex !== null) {
        // Ustaw pole dla elementu nawigacji
        // W template używamy: v-for="(navEl, subIdx) in subgroup.navigation_elements"
        // gdzie subgroup to form.navigation_subgroups[index]
        // więc navEl to form.navigation_subgroups[subgroupIndex].navigation_elements[elementIndex]
        const subgroup = this.form.navigation_subgroups?.[subgroupIndex];
        const navigationElements = subgroup?.navigation_elements;
        
        if (navigationElements && navigationElements[elementIndex]) {
          // Używamy $set na obiekcie elementu nawigacji
          // Musimy użyć pełnej ścieżki dla reaktywności Vue
          this.$set(
            this.form.navigation_subgroups[subgroupIndex].navigation_elements,
            elementIndex,
            {
              ...navigationElements[elementIndex],
              filter_field: field.field,
            }
          );
        }
      }
      
      this.currentFilterContext = null;
    },
  },
  computed: {
    availableTargetLanguages() {
      // Exclude currently selected language in the language selector
      return this.languages.filter((l) => l.lang !== this.lang.lang);
    },
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.edit-icon:hover {
  cursor: pointer;
  color: green;
}

.delete-icon:hover {
  cursor: pointer;
  color: red;
}
</style>
